import { inject, Injectable, OnDestroy } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { catchError, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { completeSubject, deepEqual, unsubscribeAll } from 'src/app/common/app.helpers';
import { FieldAttributeConstructorService } from 'src/app/forms/fields/field-attributes-constructor.service';
import { FormsAttributeService } from 'src/app/forms/forms-attribute.service';
import { SearchFormTypes } from 'src/app/forms/forms.enum';
import { FieldAttribute } from 'src/app/interfaces/form-and-field.interface';
import {
  CoordsNormalizerI,
  CovidNewStatistics,
  MapGotoBlockContent,
  MapGotoBlockForm,
  MapGotoBlockTabContent,
  MapSearchBlockContent,
  MapSearchBlockForm,
  MapSearchBlockTabContent,
  MapSearchPassedData,
  MapState,
  OverallStatistics,
  ProcessMapResponseContent,
  ProcessMapSearchResponseStrategy,
} from 'src/app/interfaces/map.interface';
import { SearchRequestObject, SearchResultResponse } from 'src/app/interfaces/search.interface';
import { MapMarkers } from 'src/app/models/map-markers.model';
import { CovidMapData, LocationObject, Statistics } from 'src/app/swagger-api';
import { DefaultService as RestApiService } from 'src/app/swagger-api/api/default.service';
import { SearchAggregationResponse } from 'src/app/swagger-api/model/searchAggregationResponse';
import { SearchResult } from 'src/app/swagger-api/model/searchResult';
import { MaybeNull } from 'src/app/utils';
import { environment } from 'src/environments/environment';
import { MultipleSearchMapFormFieldService } from '../../forms/fields/components/multiple-search-map-form-field/helpers/multiple-search-map-form-field.service';
import { SuggestionsHintsService } from '../../services/suggestions-hints.service';
import { SearchStrategyTypes } from '../search/search.enum';
import { SearchService } from '../search/search.service';
import { MapFilters, ProcessResponseStrategyTypes } from './map.enum';
import { getTabRelatedMapState, isInAggregationZoomRange } from './map.helpers';
import { FeatureCollection, FieldValue, MarkersStateChanges, SearchBlockFieldValues } from './map.interfaces';
import MapSettings from './map.settings';
import { MapValues, shouldDisableField } from './map.values';
import {
  CoordsNormalizer,
  CoordsNormalizerDecorator,
} from './markers-handler-helpers/decorators/coords-normalizer.decorator';
import { MapSearchRequestCreatorFactory } from './markers-handler-helpers/factories/map-search-request-creator.factory';
import { MarkersProcessor } from './markers-handler-helpers/helpers/markers.processor';
import { AggregatedItemsProcessor } from './markers-handler-helpers/strategies/aggregated-items-processor.strategy';
import { CovidItemsProcessor } from './markers-handler-helpers/strategies/covid-items-processor.strategy';
import { NonAggregatedItemsProcessor } from './markers-handler-helpers/strategies/non-aggregated-items-processor.strategy';
import { PeopleNonAggregatedItemsProcessor } from './markers-handler-helpers/strategies/people-non-aggregated-items-processor.strategy';
import { DefaultSearchBlockContentStrategy } from './search-block/strategy/default-search-block-content.strategy';
import { PeopleSearchBlockContentStrategy } from './search-block/strategy/people-search-block-content.strategy';
import { ProjectSearchBlockContentStrategy } from './search-block/strategy/projects-search-block-content.strategy';
import { SearchBlockContentStrategy } from './search-block/strategy/search-block-content.strategy';
import {
  CommunitiesContentStrategy,
  SpecialitiesAndDiseasesSearchBlockContentStrategy,
} from './search-block/strategy/specialities-and-diseases-search-block-content.strategy';
import { OverallStatistic } from './types/custom.types';

@Injectable()
export class MapService implements OnDestroy {
  protected currentState: MapState = MapValues.defaultMapState();
  protected previousState: MapState = structuredClone(this.currentState);
  protected aggregatedSearchActivated = true;

  protected mapMarkers: MapMarkers = new MapMarkers();

  protected markersChanges$: Subject<MarkersStateChanges> = new Subject();
  private sessionActivity$: Subject<boolean> = new Subject();
  private sessionAdmin$: Subject<boolean> = new Subject();
  private mapStateChanged$: Subject<MapState> = new Subject();
  private searchBlockTabContent$: Subject<MapSearchBlockTabContent> = new Subject();
  private goToBlockTabContent$: Subject<MapGotoBlockTabContent> = new Subject();
  private statisticsUnderMap$: Subject<OverallStatistics> = new Subject();
  private searchBlockFieldValues$: Subject<SearchBlockFieldValues> = new Subject();
  private aggregationRequestError$: Subject<void> = new Subject();

  private _resetFormTrigger$: Subject<void> = new Subject();

  private searchBlockContentStrategy: SearchBlockContentStrategy;

  private tempStatistics: MaybeNull<OverallStatistics> = null;
  private statisticsStorage: Map<string, OverallStatistics> = new Map();

  private subscriptions: Subscription[] = [];

  private searchService = inject(SearchService);
  private apiService = inject(RestApiService);
  private formsAttributeService = inject(FormsAttributeService);
  private fieldsService = inject(FieldAttributeConstructorService);
  private sessionService = inject(SessionService);
  private mapSearchFormFieldValueService = inject(MultipleSearchMapFormFieldService);
  private suggestionsHintsService = inject(SuggestionsHintsService);

  private searchBlockContent: MapSearchBlockContent = {
    formContent: {
      formAttribute: this.formsAttributeService.getFormAttribute(SearchFormTypes.MAP_SEARCH_BLOCK),
      fieldAttributes: this.fieldsService.convertAttributesMetadata(
        MapValues.mapSearchFormMetadataAttrs(),
        SearchFormTypes.MAP_SEARCH_BLOCK,
      ),
      data: {},
    },
  };

  private searchBlockFields: SearchBlockFieldValues = {
    roles: [],
    diseases: [],
    specialties: [],
    charity_focuses: [],
  };

  private goToBlockContent: MapGotoBlockContent = {
    formContent: {
      formAttribute: this.formsAttributeService.getFormAttribute(SearchFormTypes.MAP_GOTO_BLOCK),
      fieldAttributes: this.fieldsService.convertAttributesMetadata(
        MapValues.mapGotoFormMetadataAttrs(),
        SearchFormTypes.MAP_GOTO_BLOCK,
      ),
      data: {},
    },
  };

  public constructor() {
    this.initServiceValues();
  }

  public get onMarkersChanges(): Observable<MarkersStateChanges> {
    return this.markersChanges$.asObservable();
  }

  public get mapStateChanged(): Observable<MapState> {
    return this.mapStateChanged$.asObservable();
  }

  public get searchBlockTabContent(): Observable<MapSearchBlockTabContent> {
    return this.searchBlockTabContent$.asObservable();
  }

  public get goToBlockTabContent(): Observable<MapGotoBlockTabContent> {
    return this.goToBlockTabContent$.asObservable();
  }

  public get statisticsUnderMap(): Observable<OverallStatistics> {
    return this.statisticsUnderMap$.asObservable();
  }

  public get searchBlockFieldValuesObservable(): Observable<SearchBlockFieldValues> {
    return this.searchBlockFieldValues$.asObservable();
  }

  public get aggregationRequestErrorObservable(): Observable<void> {
    return this.aggregationRequestError$.asObservable();
  }

  public get sessionActivity(): Observable<boolean> {
    return this.sessionActivity$.asObservable();
  }

  public get sessionAdmin(): Observable<boolean> {
    return this.sessionAdmin$.asObservable();
  }

  public get searchBlockFormContent(): MapSearchBlockContent {
    return this.searchBlockContent;
  }

  public get goToBlockFormContent(): MapGotoBlockContent {
    return this.goToBlockContent;
  }

  public get resetFormTrigger(): Observable<void> {
    return this._resetFormTrigger$;
  }

  /**
   * @description A function that checks if Search form is submitted.
   *   Check whether current map state contains Search form keys.
   * @param { MapGotoBlockForm } state
   */
  public isSearchFormSubmitted(state: MapSearchBlockForm = this.currentState): boolean {
    let mapSearchBlockKeys: string[] = MapValues.mapSearchBlockFormKeys;
    if (this.currentState.filter !== MapFilters.PEOPLE) {
      mapSearchBlockKeys = mapSearchBlockKeys.filter((key: string) => key !== MapValues.rolesFieldName);
    }

    return state
      ? Object.entries(state)
          .filter(([_, value]: [string, string | string[] | LocationObject]) =>
            Array.isArray(value) ? value.length : Boolean(value),
          )
          .map(([key, _]: [string, string | LocationObject]) => key)
          .some((key: string) => mapSearchBlockKeys.includes(key))
      : false;
  }

  /**
   * @description A function that checks if GoTo form is submitted.
   *   Check whether current map state contains GoTo form keys.
   * @param { MapGotoBlockForm } state
   */
  public isGoToFormSubmitted(state: MapGotoBlockForm = this.currentState): boolean {
    return state
      ? Object.entries(state)
          .filter(([_, value]: [string, string | string[] | LocationObject]) =>
            Array.isArray(value) ? value.length : Boolean(value),
          )
          .map(([key, _]: [string, string | LocationObject]) => key)
          .some((key: string) => MapValues.mapGotoBlockFormKeys.includes(key))
      : false;
  }

  public isUserFilterSelected(): boolean {
    return this.currentState.filter === MapFilters.PEOPLE || this.currentState.filter === MapFilters.ORGANIZATIONS;
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions);
    completeSubject(this.markersChanges$);
    completeSubject(this.mapStateChanged$);
    completeSubject(this.goToBlockTabContent$);
    completeSubject(this.searchBlockTabContent$);
    completeSubject(this.statisticsUnderMap$);
    completeSubject(this.aggregationRequestError$);
  }

  public fetchInitialMapState(): void {
    this.emitMapState(this.currentState);
    this.sessionActivity$.next(this.sessionService.isSessionActive());
    this.sessionAdmin$.next(this.sessionService.isAdminSession());
  }

  /**
   * @description Updates the filter value in map state, cleans up markers list and
   *              re-fetches markers list using the updated map state.
   * @param { MapFilters } filter
   */
  public onFilterChanged(filter: MapFilters): void {
    this.tempStatistics = null;

    this.emitMapMarkers(true, new MapMarkers());

    this.previousState.filter = this.currentState.filter;
    this.currentState.filter = filter;

    this.updateSearchBlockContentStrategy();
    this.emitSearchBlockFieldValues();
    this.emitSearchFormContent();
    this.emitMapGotoFormContent();
    this.fetchMapData();
  }

  public updateSearchBlockContentStrategy(): void {
    if (this.searchBlockContentStrategy) {
      this.searchBlockContentStrategy.unsubscribeFromSearchBlockFieldChanges();
    }

    if (this.currentState.filter === MapFilters.PEOPLE) {
      this.searchBlockContentStrategy = new PeopleSearchBlockContentStrategy(
        this.suggestionsHintsService.individualRolesOptions,
        this.searchBlockFormContent,
        this.searchBlockFieldValuesObservable,
      );
    } else if (this.currentState.filter === MapFilters.ORGANIZATIONS) {
      this.searchBlockContentStrategy = new SpecialitiesAndDiseasesSearchBlockContentStrategy(
        this.searchBlockFormContent,
      );
    } else if (this.currentState.filter === MapFilters.COMMUNITIES) {
      this.searchBlockContentStrategy = new CommunitiesContentStrategy(this.searchBlockFormContent);
      this._resetFormTrigger$.next();
    } else if (this.currentState.filter === MapFilters.PROJECTS) {
      this.searchBlockContentStrategy = new ProjectSearchBlockContentStrategy(this.searchBlockFormContent);
    } else {
      this.searchBlockContentStrategy = new DefaultSearchBlockContentStrategy(this.searchBlockFormContent);
    }

    this.mapSearchFormFieldValueService.notifyAttributeChanged();
  }

  /**
   * @description Handles emitted map state by agm, does center coordinates normalization
   *              and re-fetches markers list using the updated map state.
   * @param { MapState } state
   * @param isMapInitialized
   */
  public onMapChanges(state: MapState, isMapInitialized: boolean): boolean {
    if (!deepEqual(this.currentState, state)) {
      this.updateCurrentMapState(state);

      const coords: CoordsNormalizerI = new CoordsNormalizer(this.currentState.location.center);
      const decoratedCoords: CoordsNormalizerI = new CoordsNormalizerDecorator(coords);
      this.currentState.location.center = decoratedCoords.getNormalizedCoords();

      /**
       * The isMapInitialised flag must be checked to ensure that the initial map data fetching process is not canceled.
       */
      if (this.shouldNotBeReloaded() && isMapInitialized) {
        return false;
      }

      this.fetchMapData();
      return true;
    } else {
      return false;
    }
  }

  /**
   * @description This method was added by someone with the comment:
   * "Check if map should be reloaded. Prevents markers blinking when going throw the map."
   *
   * In a nutshell if the user moves map on the same zoom level we shouldn't fetch -> update markers on the map (only in the aggregation zoom range).
   */
  public shouldNotBeReloaded(): boolean {
    return isInAggregationZoomRange(this.currentState.zoom) && this.currentState.zoom === this.previousState.zoom;
  }

  /**
   * @description Returns map state to default without filters.
   */
  public onResetForm(): void {
    const { filter, location, zoom } = this.currentState;
    this.updateCurrentMapState({ filter, location, zoom });
    this.fetchMapData();
    this.resetSearchBlockFields();
    this.searchBlockContentStrategy.emitSearchFormContent();
  }

  /**
   * @description Called to update the search state and map state object depend on
   *              the submitted by the user form data.
   * @param { MapSearchBlockForm } data
   */
  public onSearchFormSubmitted(data: MapSearchBlockForm): void {
    this.onSubmitFormFetchMapData({ ...this.currentState, ...data });
  }

  public onMapGotoFormSubmitted(data: MapGotoBlockForm): void {
    const mapState: MapState = this.getStateWithUpdatedLocation(data, {
      ...data,
      ...this.currentState,
    });

    this.emitMapState(mapState);
  }

  /**
   * @description Builds and emits to subscribers the expected for search block container
   *              form content depend on the current map state filter value.
   */
  public emitSearchFormContent(): void {
    const attrs: FieldAttribute[] = this.searchBlockContent.formContent.fieldAttributes;
    this.searchBlockContentStrategy.emitSearchFormContent();
    this.searchBlockContent.formContent.fieldAttributes = attrs;
    this.searchBlockContent.title = MapValues.searchBlockTitleMapper[this.currentState.filter];
    this.searchBlockTabContent$.next({
      fieldAttributes: attrs,
      title: this.searchBlockContent.title,
    });
  }

  /**
   * @description Builds and emits to subscribers the expected for search block container
   *              form content depend on the current map state filter value.
   */
  public emitMapGotoFormContent(): void {
    const attrs: FieldAttribute[] = this.goToBlockContent.formContent.fieldAttributes;

    this.goToBlockContent.formContent.fieldAttributes = attrs;
    this.goToBlockContent.title = MapValues.searchBlockTitleMapper[this.currentState.filter];
    this.goToBlockTabContent$.next({
      fieldAttributes: attrs,
      title: this.goToBlockContent.title,
    });
  }

  public updateCurrentMapState(state: MapState): void {
    this.previousState = structuredClone(this.currentState);
    this.currentState = state;
  }

  /**
   * @description A custom handler for fetching the map data on submit form event.
   *              Emits incoming map state to subscribers and calls corresponding method
   *              to fetch map data in case zoom level value wasn't changed.
   * @param { MapState } state
   */
  public onSubmitFormFetchMapData(state: MapState): void {
    this.emitMapState(state);

    if (state.zoom === this.currentState.zoom) {
      this.updateCurrentMapState(state);
      this.fetchMarkersWrapper();
    }
  }

  public getSearchResultWithoutLocation(): void {
    const currentState: MapState = this.currentState;
    const previousState: MapState = this.previousState;

    this.searchService.getSearchResults({
      state: { data: { currentState, previousState } },
      applyLocationAttrs: false,
    });
  }

  public searchLocationLiteralMarkers(locationLiteral: string): void {
    const currentState: MapState = this.currentState;
    const previousState: MapState = this.previousState;

    this.searchService.getSearchResults({
      state: { data: { currentState, previousState } },
      applyLocationAttrs: false,
      locationLiteral,
    });
  }

  /**
   * @description Default handler of the map data fetching.
   */
  public fetchMapData(): void {
    this.emitMapState(this.currentState);

    this.fetchMarkersWrapper();
  }

  /**
   * @description Fetches the appropriate to passed filter type statistics.
   * @param { MapFilters } filter
   */
  public updateDisplayedStatistics(filter: MapFilters = this.currentState.filter): void {
    if (filter !== MapFilters.COVID) {
      this.updateMapStatistics();
    }
  }

  /**
   * @description Emits a copy of the passed state to subscribers.
   * @param { MapState } state
   */
  public emitMapState(state: MapState): void {
    this.mapStateChanged$.next(structuredClone(state));
  }

  protected getStateWithUpdatedLocation(data: MapGotoBlockForm, state: MapState): MapState {
    state.form_location = data.form_location;
    state.location.center = data.form_location.center;
    state.location.northWestCoords = { lat: data.viewport.northeast.lat, lon: data.viewport.southwest.lon };
    state.location.southEastCoords = { lat: data.viewport.southwest.lat, lon: data.viewport.northeast.lon };
    state.zoom = MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL;
    return state;
  }

  /**
   * @description Depend on the current map state's filter value calls the appropriate
   *              method in order to update the list of markers if map states aren't equal.
   * @param { MapState } currentState
   * @param { MapState } previousState
   */
  protected fetchMarkers(currentState: MapState, previousState: MapState): void {
    if (!deepEqual(currentState, previousState)) {
      currentState.filter === MapFilters.COVID
        ? this.fetchCovidMapData(currentState, previousState)
        : this.search(currentState, previousState);
      return;
    }

    const markerStatusChanges = { markers: this.mapMarkers, reset: false };
    this.markersChanges$.next(markerStatusChanges);
  }

  /**
   * @description Initiates the API call to re-fetch aggregated markers according to
   *              selected filters and notify subscribers about changes in case truthy
   *              aggregatedSearchActivated flag value.
   * @param { MapState } currentState
   * @param { MapState } previousState
   */
  protected fetchAggregatedItems(currentState: MapState, previousState: MapState): void {
    const request: SearchRequestObject = MapSearchRequestCreatorFactory.createAggregatedMapSearchRequest(currentState);

    this.apiService.restSearchAggregationsGet(JSON.stringify(request)).subscribe({
      next: (response: SearchAggregationResponse) => {
        if (this.aggregatedSearchActivated) {
          this.processSearchResponse({
            type: ProcessResponseStrategyTypes.AGGREGATED,
            response,
            state: { currentState, previousState },
          });
        }
      },
      error: () => {
        this.aggregationRequestError$.next();
      },
    });
  }

  /**
   * @description Initiates the API call to re-fetch covid markers according to
   *              current zoom level and location. Notifies subscribers about changes.
   * @param { MapState } currentState
   * @param { MapState } previousState
   */
  protected fetchCovidMapData(currentState: MapState, previousState: MapState): void {
    const request: SearchRequestObject = MapSearchRequestCreatorFactory.createCovidMapSearchRequest(currentState);

    this.apiService
      .restMapCovidGet(JSON.stringify(request))
      .pipe(catchError(() => EMPTY))
      .subscribe((response: CovidMapData) => {
        response.total_stats['updated'] = response.updated_at;
        const covidStatistics: CovidNewStatistics = response.total_stats;

        this.emitStatisticsUnderMap({
          statistic_type: OverallStatistic.Covid,
          statistic_counters: covidStatistics,
        });

        this.processSearchResponse({
          type: ProcessResponseStrategyTypes.COVID,
          response: JSON.parse(response.data) as SearchAggregationResponse | SearchResult[] | FeatureCollection,
          state: { currentState, previousState },
        });
      });
  }

  /**
   * @description Depend on zoom level value calls the appropriate method to update
   *              the markers list according to passed filters.
   * @param { MapState } currentState
   * @param { MapState } previousState
   */
  protected search(currentState: MapState, previousState: MapState): void {
    this.updateDisplayedStatistics();

    this.aggregatedSearchActivated = isInAggregationZoomRange(currentState.zoom);

    if (this.aggregatedSearchActivated) {
      this.fetchAggregatedItems(currentState, previousState);
      return;
    }

    this.searchService.getSearchResults({
      state: { data: { currentState, previousState } },
      applyLocationAttrs: true,
    });
  }

  protected processSearchResponse(content: ProcessMapResponseContent): void {
    const strategyMapper: {
      [key: string]: ProcessMapSearchResponseStrategy;
    } = {
      [ProcessResponseStrategyTypes.COVID]: new CovidItemsProcessor((content.response as FeatureCollection).features),
      [ProcessResponseStrategyTypes.AGGREGATED]: new AggregatedItemsProcessor(
        content.response as SearchAggregationResponse,
        content.state.currentState,
      ),
      [ProcessResponseStrategyTypes.NON_AGGREGATED]: new NonAggregatedItemsProcessor(
        content.response as SearchResult[],
        content.state.currentState,
      ),
      [ProcessResponseStrategyTypes.PEOPLE_NON_AGGREGATED]: new PeopleNonAggregatedItemsProcessor(
        content.response as SearchResult[],
        content.state.currentState,
      ),
    };
    const strategy: ProcessMapSearchResponseStrategy = strategyMapper[content.type];
    content.markers = strategy.getMarkers();

    this.emitMapMarkers(false, MarkersProcessor.processMarkers(content, this.mapMarkers));
  }

  /**
   * @description Depend on zoom level value calls the appropriate method to update
   *              the markers list according to passed filters.
   * @param { boolean } reset - if the tab was switched, and it's necessary to reset markers on the map to wait until the markers will be fetched for the new MapFilters value - pass 'true'. In other cases - pass 'false'.
   * @param { MapMarkers } mapMarkers
   * @param { MapMarkers } markersToEmit
   */
  protected emitMapMarkers(reset: boolean, mapMarkers: MapMarkers, markersToEmit?: MapMarkers): void {
    this.mapMarkers = mapMarkers;

    const markersStateChanges = {
      markers: markersToEmit || this.mapMarkers,
      reset: reset,
    };

    this.markersChanges$.next(markersStateChanges);
  }

  private initServiceValues(): void {
    this.subscribeToSearchChanges();
    this.subscribeToSessionChanges();
    this.updateSearchBlockContentStrategy();
    this.subscribeToSearchFormContentChanges();
  }

  private emitSearchBlockFieldValues(): void {
    this.searchBlockFieldValues$.next(this.searchBlockFieldValues);
  }

  private resetSearchBlockFields(): void {
    this.searchBlockFields = {
      roles: [],
      diseases: [],
      specialties: [],
      charity_focuses: [],
    };
    this.emitSearchBlockFieldValues();
  }

  /**
   * @description Saves passed statistics to service storage in case saveStatistics flag
   *              truthy value and notifies subscribers about changes.
   * @param { OverallStatistics } statistics
   * @param { boolean } saveStatistics
   */
  private emitStatisticsUnderMap(
    statistics: OverallStatistics,
    saveStatistics = true,
    saveTempStatistics = false,
  ): void {
    if (saveStatistics) {
      this.statisticsStorage.set(this.currentState.filter, statistics);
    } else {
      if (saveTempStatistics) {
        this.tempStatistics = structuredClone(statistics);
      }
    }

    this.statisticsUnderMap$.next(structuredClone(statistics));
  }

  /**
   * @description Returns result of check if the storage contains the statistic with
   *              current filter key. Also emits fetched from the storage statistics to
   *              subscribers in case truthy condition.
   * @param { boolean } notifySubscribers
   * @returns { boolean }
   */
  private hasTabStatisticsInStorage(notifySubscribers = true): boolean {
    if (notifySubscribers && this.statisticsStorage.has(this.currentState.filter)) {
      this.emitStatisticsUnderMap(this.statisticsStorage.get(this.currentState.filter), false);
      return true;
    }

    return false;
  }

  /**
   * @description Emits fetched from the storage internal app statistics to subscribers in
   *              case truthy condition, emits saved on the previous stem tempStatistics
   *              in case map state filters except location and zoom props weren't changed,
   *              or initiates the API call to re-fetch it.
   */
  private updateMapStatistics(): void {
    if (this.hasTabStatisticsInStorage(!this.isSearchFormSubmitted())) {
      return;
    }

    if (
      this.tempStatistics &&
      deepEqual(getTabRelatedMapState(this.currentState), getTabRelatedMapState(this.previousState))
    ) {
      this.emitStatisticsUnderMap(this.tempStatistics, false);
      return;
    }

    this.fetchMapStatistics();
  }

  /**
   * @description Does additional coords check before calling the fetchMarkers() method.
   */
  private fetchMarkersWrapper(): void {
    if (!this.currentState.location.northWestCoords || !this.currentState.location.southEastCoords) {
      return;
    }

    this.fetchMarkers(this.currentState, this.previousState);
  }

  /**
   * @description Initiates the API call to re-fetch internal app statistics and notify
   *              subscribers about changes.
   */
  private fetchMapStatistics(): void {
    const wasSubmitted: boolean = this.isSearchFormSubmitted();
    const request: SearchRequestObject = MapSearchRequestCreatorFactory.createMapStatisticsSearchRequest(
      this.currentState,
      wasSubmitted,
    );

    this.apiService
      .restMapStatisticsGet(JSON.stringify(request))
      .pipe(catchError(() => EMPTY))
      .subscribe((response: Statistics) =>
        this.emitStatisticsUnderMap(response as OverallStatistics, !wasSubmitted, true),
      );
  }

  /**
   * @description Subscribes on searchResultsChanges event, converts emits the updated
   *              markers list.
   */
  private subscribeToSearchChanges(): void {
    this.searchService.setStrategy(SearchStrategyTypes.MAP);

    this.subscriptions.push(
      this.searchService.searchResultsChanges.subscribe((response: SearchResultResponse) => {
        if (this.currentState.filter === MapFilters.PEOPLE && !this.aggregatedSearchActivated) {
          this.processSearchResponse({
            type: ProcessResponseStrategyTypes.PEOPLE_NON_AGGREGATED,
            response: response.search_results,
            state: response.params as MapSearchPassedData,
            totalMatchesCount: response.total_matches_count,
          });
        } else if (!this.aggregatedSearchActivated) {
          this.processSearchResponse({
            type: ProcessResponseStrategyTypes.NON_AGGREGATED,
            response: response.search_results,
            state: response.params as MapSearchPassedData,
            totalMatchesCount: response.total_matches_count,
          });
        }
      }),
    );
  }

  /**
   * @description Subscribes to sessionChanges event and updates isLoggedIn value on changes.
   */
  private subscribeToSessionChanges(): void {
    this.subscriptions.push(
      this.sessionService.sessionChanges.subscribe(() => {
        const isActive: boolean = this.sessionService.isSessionActive();
        this.sessionActivity$.next(isActive);
        this.sessionAdmin$.next(this.sessionService.isAdminSession());

        if (!isActive && !this.aggregatedSearchActivated && this.isUserFilterSelected()) {
          this.emitMapMarkers(true, new MapMarkers());
        }
      }),
    );
  }

  private updateSearchBlockFieldValues(fieldValue: FieldValue): void {
    if (fieldValue.field === MapValues.diseasesFieldName) {
      this.searchBlockFields.diseases = fieldValue.selectedItems;
    }
    if (fieldValue.field === MapValues.specialtiesFieldName) {
      this.searchBlockFields.specialties = fieldValue.selectedItems;
    }
    if (fieldValue.field === MapValues.rolesFieldName) {
      this.searchBlockFields.roles = fieldValue.selectedItems;
    }
    if (fieldValue.field === MapValues.charityFocus) {
      this.searchBlockFields.charity_focuses = fieldValue.selectedItems;
    }
  }

  public get searchBlockFieldValues(): SearchBlockFieldValues {
    return this.searchBlockFields;
  }

  private subscribeToSearchFormContentChanges(): void {
    this.subscriptions.push(
      this.mapSearchFormFieldValueService.fieldValueChanged.subscribe((value: FieldValue) => {
        this.updateFormAttributes(value);
        this.updateSearchBlockFieldValues(value);
        this.emitSearchBlockFieldValues();
      }),
    );
  }

  private updateFormAttributes(value: FieldValue): void {
    const fieldAttributes = this.searchBlockContent.formContent.fieldAttributes;

    fieldAttributes.forEach((item) => {
      item.disabled = shouldDisableField(item.backendAttribute.name as keyof SearchBlockFieldValues, value);
    });

    this.searchBlockTabContent$.next({
      fieldAttributes,
      title: this.searchBlockContent.title,
    });
  }
}

export enum MapLibraries {
  CORE = 'core',
  MAPS = 'maps',
  PLACES = 'places',
  GEOGODING = 'geocoding',
}

@Injectable({
  providedIn: 'root',
})
export class MapsAPILoader {
  public async load(library?: MapLibraries): Promise<boolean> {
    return new Loader({ apiKey: environment.google.apiKey })
      .importLibrary(library || MapLibraries.CORE)
      .then(() => true)
      .catch(() => false);
  }
}
